'use client';
import { Button, Typography } from '@curology/ui-components-web/react';
import { CardRating } from '@curology/ui-components-web/react/Card/CardRating';
import { MinusIcon } from '@curology/ui-components-web/react/Icon/icons/MinusIcon';
import { PlusIcon } from '@curology/ui-components-web/react/Icon/icons/PlusIcon';
import Image from 'next/image';
import React from 'react';
import Slider from 'react-slick';

import '../css/carousel.css';

import { type AddOnProductType } from '@/components/flow/expressCurologySignUpSkin/types';
import {
  allArrayValuesInOtherArray,
  getFormattedPrice,
} from '@/components/flow/expressCurologySignUpSkin/utils';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';

const getSwipeDirection = (
  currentIndex: number,
  nextIndex: number,
  lastIndex: number,
) => {
  if (currentIndex === 0 && nextIndex === lastIndex) {
    return 'backward';
  }

  if (currentIndex === lastIndex && nextIndex === 0) {
    return 'forward';
  }

  if (currentIndex > nextIndex) {
    return 'backward';
  }

  return 'forward';
};

export const AddOnProductCard = ({
  addAddOnProduct,
  addOnProduct,
  isSubmitting,
  offerId,
  removeAddOnProduct,
  selectedAddOnProductItemIds,
}: {
  addAddOnProduct: (addOnProduct: AddOnProductType) => void;
  addOnProduct: AddOnProductType;
  isSubmitting: boolean;
  offerId: number;
  removeAddOnProduct: (addOnProduct: AddOnProductType) => void;
  selectedAddOnProductItemIds: number[];
}) => {
  return (
    <li className="cur-basis-[50%] cur-pr-4" key={addOnProduct.id}>
      <div className="cur-bg-surface-neutral cur-flex cur-flex-col cur-h-full cur-p-3 cur-relative">
        <div className="add-on-product-card-image-carousel-container cur-aspect-[140/175]">
          <div className="add-on-product-card-image-carousel-inner-container">
            <Slider
              accessibility={true}
              arrows={false}
              beforeChange={(current: number, next: number) => {
                trackEvent(AnalyticsEvent.PRODUCT_CARD_CAROUSEL_SWIPED, {
                  flow: FlowTypes.PDP_CHECKOUT,
                  imageIndex: next,
                  offerId,
                  productName: addOnProduct.name,
                  swipeDirection: getSwipeDirection(
                    current,
                    next,
                    addOnProduct.carouselImages.length - 1,
                  ),
                });
              }}
              dots={true}
              draggable={true}
              infinite={true}
            >
              {addOnProduct.carouselImages.map((carouselImage) => {
                return (
                  <Image
                    alt={carouselImage.altText}
                    height={350}
                    key={carouselImage.url}
                    quality={100}
                    src={carouselImage.url}
                    width={280}
                  />
                );
              })}
            </Slider>
          </div>
        </div>
        <div className="cur-flex-1 cur-py-3">
          <Typography className="cur-mb-3" weight="bold">
            {addOnProduct.name}
          </Typography>
          <div className="cur-flex cur-items-center cur-mb-3">
            <Typography
              className="cur-mr-1 cur-uppercase"
              size="meta"
              weight="bold"
            >
              {getFormattedPrice(addOnProduct.price.final)}
            </Typography>
            <Typography className="cur-text-disabled" size="meta">
              <span className="cur-line-through">
                {getFormattedPrice(addOnProduct.price.original)}
              </span>
            </Typography>
          </div>
          <Typography size="meta">{addOnProduct.volume}</Typography>
          <Typography className="cur-mb-3" size="meta">
            {addOnProduct.cadence}
          </Typography>
          <div>
            <CardRating
              count={addOnProduct.numReviews}
              showCount={false}
              size="sm"
              value={addOnProduct.rating}
              readOnly
            />
          </div>
        </div>
        <div>
          {allArrayValuesInOtherArray(
            addOnProduct.itemIds,
            selectedAddOnProductItemIds,
          ) ? (
            <Button
              className="cur-w-full"
              disabled={isSubmitting}
              onClick={() => removeAddOnProduct(addOnProduct)}
              type="button"
            >
              <MinusIcon size="sm" />
              Remove
            </Button>
          ) : (
            <Button
              buttonType="secondary"
              className="cur-w-full"
              disabled={isSubmitting}
              onClick={() => addAddOnProduct(addOnProduct)}
              type="button"
            >
              <PlusIcon size="sm" />
              Add
            </Button>
          )}
        </div>
      </div>
    </li>
  );
};
